import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, MoreHorizontal, Trash2, Building2, ChevronLeft, ChevronRight, ArrowLeft, Music, ListPlus, ListMinus, Edit, Play, Pause, Search, ListMusic, Star } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { formatDurationWithHours } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Playlist } from "@/types/playlist";
import { Company } from '@/types/company';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { usePlayerStore } from "@/store/playerStore";
import { GlobalFooterPlayer } from "@/components/player/GlobalFooterPlayer";
import { AssignCompaniesModal } from "@/components/playlists/AssignCompaniesModal";
import { CreatePlaylistModal } from "@/components/playlists/CreatePlaylistModal";
import { PlaylistStats } from "@/components/playlists/PlaylistStats";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AddSongsToPlaylistModal } from "@/components/playlists/AddSongsToPlaylistModal";
import { SendToHeroDialog } from "@/components/playlists/SendToHeroDialog";
import { BulkAssignPlaylistsModal } from "@/components/playlists/BulkAssignPlaylistsModal";
import { heroPlaylistService } from "@/services/heroPlaylistService";

export default function PlaylistPage() {
  const [playlistToDelete, setPlaylistToDelete] = useState<string | null>(null);
  const [playlistToAssign, setPlaylistToAssign] = useState<Playlist | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState<Playlist | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'public' | 'private' | 'hero'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [detailView, setDetailView] = useState(false);
  const [currentPlaylistDetail, setCurrentPlaylistDetail] = useState<Playlist | null>(null);
  const [songSearchQuery, setSongSearchQuery] = useState('');
  const [selectedSongs, setSelectedSongs] = useState<string[]>([]);
  const [isAddSongsModalOpen, setIsAddSongsModalOpen] = useState(false);
  const [isSendToHeroDialogOpen, setIsSendToHeroDialogOpen] = useState(false);
  const [playlistForHero, setPlaylistForHero] = useState<Playlist | null>(null);
  const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false);

  // Doğrudan düzenleme için state'ler
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [artworkFile, setArtworkFile] = useState<File | null>(null);
  
  const itemsPerPage = 20;
  const { toast } = useToast();

  // Player store'dan gerekli değerleri ve fonksiyonları al
  const { playSong, pauseSong, currentSong, isPlaying } = usePlayerStore();

  // Şarkıyı çal
  const handlePlaySong = (song: any) => {
    // Şarkı zaten çalıyorsa durdur, değilse çal
    if (isPlaying && currentSong?._id === song._id) {
      pauseSong();
    } else {
      // Şarkı için artwork URL'ini ayarla
      const songWithArtwork = {
        ...song,
        artwork: song.artworkUrl // Player bileşeni artwork bekliyor olabilir
      };
      playSong(songWithArtwork);
    }
  };

  // Playlist verilerini çek
  const { data: playlists, isLoading, error, refetch } = useQuery({
    queryKey: ["playlists"],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/playlists`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) {
        throw new Error("Playlist verileri alınamadı");
      }
      const data = await response.json();
      console.log("Playlists data:", data); // Debug için ekledik
      return data;
    },
  });

  // Hero playlist'leri çek
  const { data: heroPlaylists = [], refetch: refetchHero } = useQuery({
    queryKey: ["heroPlaylists"],
    queryFn: () => heroPlaylistService.getAll(),
  });

  // Hero'daki playlist ID'lerini Set olarak tut
  const heroPlaylistIds = new Set(heroPlaylists.map(hp => hp.playlist._id));

  // Playlistleri filtrele
  const filteredPlaylists = playlists?.filter((playlist: Playlist) => {
    const matchesSearch = playlist.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter =
      filterType === 'all' ||
      (filterType === 'public' && playlist.isPublic === true) ||
      (filterType === 'private' && playlist.isPublic === false) ||
      (filterType === 'hero' && heroPlaylistIds.has(playlist._id));

    return matchesSearch && matchesFilter;
  }) || [];

  // Playlist silme işlemi
  const handleDelete = async (id: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/playlists/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Playlist silinemedi');
      }

      toast({
        title: 'Başarılı',
        description: 'Playlist başarıyla silindi',
      });

      refetch();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Playlist silinirken bir hata oluştu',
      });
    } finally {
      setPlaylistToDelete(null);
    }
  };

  // Doğrudan playlist adını güncelle
  const handleUpdatePlaylistName = async () => {
    if (!currentPlaylistDetail) return;
    
    try {
      setIsUpdating(true);
      
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/playlists/${currentPlaylistDetail._id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ 
            name: editedName,
          })
        }
      );

      if (!response.ok) {
        throw new Error('Playlist adı güncellenemedi');
      }

      // Güncel playlist bilgilerini detaylı olarak al
      const detailResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/playlists/${currentPlaylistDetail._id}?populate=songs`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!detailResponse.ok) {
        throw new Error('Playlist detayları alınamadı');
      }
      
      // Güncel ve populate edilmiş playlist bilgilerini al
      const detailedPlaylist = await detailResponse.json();
      
      // Eğer şarkı listesi boş gelirse, mevcut şarkı listesini koruyalım
      if (detailedPlaylist && (!detailedPlaylist.songs || detailedPlaylist.songs.length === 0) && currentPlaylistDetail?.songs) {
        detailedPlaylist.songs = currentPlaylistDetail.songs;
      }
      
      setCurrentPlaylistDetail(detailedPlaylist);
      setIsEditingName(false);
      
      toast({
        title: 'Başarılı',
        description: 'Playlist adı güncellendi',
      });
      
      // Ana playlist listesini de güncelle
      refetch();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Playlist adı güncellenirken bir hata oluştu',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Doğrudan playlist açıklamasını güncelle
  const handleUpdatePlaylistDescription = async () => {
    if (!currentPlaylistDetail) return;
    
    try {
      setIsUpdating(true);
      
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/playlists/${currentPlaylistDetail._id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ 
            description: editedDescription,
          })
        }
      );

      if (!response.ok) {
        throw new Error('Playlist açıklaması güncellenemedi');
      }

      // Güncel playlist bilgilerini detaylı olarak al
      const detailResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/playlists/${currentPlaylistDetail._id}?populate=songs`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!detailResponse.ok) {
        throw new Error('Playlist detayları alınamadı');
      }
      
      // Güncel ve populate edilmiş playlist bilgilerini al
      const detailedPlaylist = await detailResponse.json();
      
      // Eğer şarkı listesi boş gelirse, mevcut şarkı listesini koruyalım
      if (detailedPlaylist && (!detailedPlaylist.songs || detailedPlaylist.songs.length === 0) && currentPlaylistDetail?.songs) {
        detailedPlaylist.songs = currentPlaylistDetail.songs;
      }
      
      setCurrentPlaylistDetail(detailedPlaylist);
      setIsEditingDescription(false);
      
      toast({
        title: 'Başarılı',
        description: 'Playlist açıklaması güncellendi',
      });
      
      // Ana playlist listesini de güncelle
      refetch();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Playlist açıklaması güncellenirken bir hata oluştu',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Artwork güncelleme
  const handleArtworkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setArtworkFile(file);
      handleUpdateArtwork(file);
    }
  };

  const handleUpdateArtwork = async (file: File) => {
    if (!currentPlaylistDetail) return;
    
    try {
      setIsUpdating(true);
      
      const formData = new FormData();
      formData.append('artwork', file);
      
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/playlists/${currentPlaylistDetail._id}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        }
      );

      if (!response.ok) {
        throw new Error('Playlist kapak resmi güncellenemedi');
      }

      // Artwork güncellemesi başarılı, şimdi playlist detaylarını tekrar alalım
      const detailResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/playlists/${currentPlaylistDetail._id}?populate=songs`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!detailResponse.ok) {
        throw new Error('Playlist detayları alınamadı');
      }
      
      // Güncel ve populate edilmiş playlist bilgilerini al
      const detailedPlaylist = await detailResponse.json();
      
      // Eğer şarkı listesi boş gelirse, mevcut şarkı listesini koruyalım
      if (detailedPlaylist && (!detailedPlaylist.songs || detailedPlaylist.songs.length === 0) && currentPlaylistDetail?.songs) {
        detailedPlaylist.songs = currentPlaylistDetail.songs;
      }
      
      setCurrentPlaylistDetail(detailedPlaylist);
      
      toast({
        title: 'Başarılı',
        description: 'Playlist kapak resmi güncellendi',
      });
      
      // Ana playlist listesini de güncelle
      refetch();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Playlist kapak resmi güncellenirken bir hata oluştu',
      });
    } finally {
      setIsUpdating(false);
      setArtworkFile(null);
    }
  };

  // Playlist detayını görüntüle
  const handleViewPlaylistDetail = async (playlist: Playlist) => {
    try {
      // Playlist detaylarını API'den al (şarkı listesi için)
      const response = await fetch(`${import.meta.env.VITE_API_URL}/playlists/${playlist._id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error("Playlist detayları alınamadı");
      }

      const detailedPlaylist = await response.json();
      setCurrentPlaylistDetail(detailedPlaylist);
      setDetailView(true);
      // Seçili şarkıları temizle
      setSelectedSongs([]);
      setSongSearchQuery('');
      
      // Düzenleme state'lerini ayarla
      setEditedName(detailedPlaylist.name || '');
      setEditedDescription(detailedPlaylist.description || '');
      setIsEditingName(false);
      setIsEditingDescription(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Playlist detayları yüklenirken bir hata oluştu',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex-1">
        <div className="flex-1 space-y-6 p-8 pt-6">
          {/* Hero Section Skeleton */}
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <div
              className="flex"
              style={{
                background: 'linear-gradient(to right, #7c3aed, #a855f7)',
              }}
            >
              <div className="p-8 flex justify-between items-start relative w-full">
                <div className="space-y-3 flex-1">
                  <Skeleton className="h-5 w-40 bg-white/20" />
                  <Skeleton className="h-9 w-64 bg-white/30" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[500px] bg-white/20" />
                    <Skeleton className="h-4 w-[450px] bg-white/20" />
                    <Skeleton className="h-4 w-[400px] bg-white/20" />
                  </div>
                </div>
                <Skeleton className="w-40 h-40 rounded-lg bg-white/10 ml-8" />
              </div>
            </div>
          </div>

          {/* PlaylistStats skeleton - Birinci satır kartları */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="overflow-hidden border border-slate-200 shadow-sm bg-white rounded-xl">
                <CardContent className="p-0">
                  <div className="relative">
                    <div className="absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full bg-slate-200 blur-2xl"></div>
                    <div className="p-6 relative">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <Skeleton className="h-4 w-[100px] mb-2" />
                          <Skeleton className="h-9 w-[50px]" />
                        </div>
                        <Skeleton className="h-14 w-14 rounded-xl" />
                      </div>
                      <div className="pt-3 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Skeleton className="w-2 h-2 rounded-full mr-2" />
                            <Skeleton className="h-4 w-[80px]" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* PlaylistStats skeleton - İkinci satır kartları */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="overflow-hidden border border-slate-200 shadow-sm bg-white rounded-xl">
                <CardContent className="p-0">
                  <div className="relative">
                    <div className="absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full bg-slate-200 blur-2xl"></div>
                    <div className="p-6 relative">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <Skeleton className="h-4 w-[100px] mb-2" />
                          <Skeleton className="h-9 w-[50px]" />
                        </div>
                        <Skeleton className="h-14 w-14 rounded-xl" />
                      </div>
                      <div className="pt-3 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Skeleton className="w-2 h-2 rounded-full mr-2" />
                            <Skeleton className="h-4 w-[80px]" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Filtreler Skeleton */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex gap-3 items-center">
              <div className="relative flex-1">
                <Skeleton className="h-11 w-full rounded-lg" />
              </div>
              <Skeleton className="h-11 w-[180px] rounded-lg" />
              <Skeleton className="h-11 w-[160px] rounded-lg" />
            </div>
          </div>

          {/* Toolbar Skeleton */}
          <div className="flex items-center">
            <Skeleton className="h-5 w-[180px]" />
          </div>

          {/* Tablo Skeleton */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <Table className="w-full table-fixed">
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                  <TableHead className="py-4 pl-6 w-[280px]">
                    <Skeleton className="h-5 w-[120px]" />
                  </TableHead>
                  <TableHead className="py-4 w-[100px]">
                    <Skeleton className="h-5 w-[80px]" />
                  </TableHead>
                  <TableHead className="py-4 w-[100px]">
                    <Skeleton className="h-5 w-[80px]" />
                  </TableHead>
                  <TableHead className="py-4 w-[100px]">
                    <Skeleton className="h-5 w-[60px]" />
                  </TableHead>
                  <TableHead className="py-4 pr-6 w-[80px] text-right">
                    <Skeleton className="h-5 w-[60px] ml-auto" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i} className="border-b border-slate-100">
                    <TableCell className="py-4 pl-6">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-12 w-12 rounded-md" />
                        <div className="space-y-1">
                          <Skeleton className="h-5 w-[140px]" />
                          <Skeleton className="h-4 w-[100px]" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-4">
                      <Skeleton className="h-5 w-[70px]" />
                    </TableCell>
                    <TableCell className="py-4">
                      <Skeleton className="h-5 w-[70px]" />
                    </TableCell>
                    <TableCell className="py-4">
                      <Skeleton className="h-5 w-[50px]" />
                    </TableCell>
                    <TableCell className="py-4 pr-6 text-right">
                      <Skeleton className="h-9 w-9 rounded-lg ml-auto" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Skeleton */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <Skeleton className="h-5 w-[250px]" />
            <div className="flex items-center gap-2">
              <Skeleton className="h-10 w-[100px] rounded-lg" />
              <div className="flex items-center gap-1">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} className="h-10 w-10 rounded-lg" />
                ))}
              </div>
              <Skeleton className="h-10 w-[100px] rounded-lg" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex-1">
        <div className="flex-1 space-y-4 p-8 pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold tracking-tight">Playlist Yönetimi</h2>
              <p className="text-muted-foreground">
                Playlistleri ve şarkıları yönetin
              </p>
            </div>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              size="lg"
              className="shrink-0"
            >
              <Plus className="mr-2 h-4 w-4" /> Yeni Playlist
            </Button>
          </div>

          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 flex justify-between items-center">
            <span>Playlist verileri alınırken bir hata oluştu.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Tekrar Dene
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleBackToList = () => {
    setDetailView(false);
    setCurrentPlaylistDetail(null);
    setSelectedSongs([]);
  };

  const handleSongSelection = (songId: string) => {
    setSelectedSongs(prev => {
      if (prev.includes(songId)) {
        return prev.filter(id => id !== songId);
      } else {
        return [...prev, songId];
      }
    });
  };

  const handleSelectAllSongs = (select: boolean) => {
    if (select && currentPlaylistDetail?.songs) {
      setSelectedSongs(currentPlaylistDetail.songs.map(song => song._id));
    } else {
      setSelectedSongs([]);
    }
  };

  const handleRemoveSongsFromPlaylist = async () => {
    if (!currentPlaylistDetail || selectedSongs.length === 0) return;

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/playlists/${currentPlaylistDetail._id}/songs`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ 
            songs: selectedSongs,
            updateForAllCompanies: true
          })
        }
      );

      if (!response.ok) {
        throw new Error('Şarkılar çıkarılamadı');
      }

      // Playlist detaylarını yeniden yükle
      const updatedResponse = await fetch(`${import.meta.env.VITE_API_URL}/playlists/${currentPlaylistDetail._id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!updatedResponse.ok) {
        throw new Error("Güncel playlist detayları alınamadı");
      }

      const updatedPlaylist = await updatedResponse.json();
      setCurrentPlaylistDetail(updatedPlaylist);
      setSelectedSongs([]);

      toast({
        title: 'Başarılı',
        description: `${selectedSongs.length} şarkı playlist'ten çıkarıldı`,
      });

      // Ana playlist listesini de güncelle
      refetch();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Şarkılar çıkarılırken bir hata oluştu',
      });
    }
  };

  const filteredSongs = currentPlaylistDetail?.songs.filter(song => 
    (song.name && song.name.toLowerCase().includes(songSearchQuery.toLowerCase())) ||
    (song.artist && song.artist.toLowerCase().includes(songSearchQuery.toLowerCase())) ||
    (song.album && song.album.toLowerCase().includes(songSearchQuery.toLowerCase()))
  ) || [];

  return (
    <div className="h-full flex-1">
      <div className="flex-1 space-y-6 p-8 pt-6">
        {!detailView ? (
          // Playlist Liste Görünümü
          <>
            {/* Hero Section */}
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <div
                className="flex"
                style={{
                  background: 'linear-gradient(to right, #7c3aed, #a855f7)',
                }}
              >
                <div className="p-8 flex justify-between items-start relative w-full">
                  <div className="space-y-3 flex-1">
                    <div className="text-white/80 text-sm font-medium">
                      Müzik Yönetim Paneli
                    </div>
                    <h1 className="text-3xl font-bold text-white">
                      Playlist Yönetimi
                    </h1>
                    <p className="text-white/90 text-sm max-w-xl leading-relaxed">
                      Tüm playlistleri görüntüleyin, yeni playlist oluşturun, şarkı ekleyin
                      ve playlistleri firmalara atayın. Müzik koleksiyonunuzu buradan yönetin.
                    </p>
                  </div>
                  <div
                    className="w-40 h-40 rounded-lg overflow-hidden relative z-10 flex items-center justify-center ml-8"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <ListMusic className="w-20 h-20 text-white" strokeWidth={1.5} />
                  </div>
                </div>
              </div>
            </div>

            {/* İstatistikler */}
            <PlaylistStats playlists={playlists || []} />

            {/* Filtreler */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex gap-3 items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    placeholder="Playlist adı ile arayın..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-11 border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <Select value={filterType} onValueChange={(value: 'all' | 'public' | 'private' | 'hero') => setFilterType(value)}>
                  <SelectTrigger className="h-11 w-[180px] rounded-lg border-slate-200">
                    <SelectValue placeholder="Tümünü göster" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümünü göster</SelectItem>
                    <SelectItem value="hero">Hero Playlistler</SelectItem>
                    <SelectItem value="public">Açık Playlistler</SelectItem>
                    <SelectItem value="private">Özel Playlistler</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={() => setIsBulkAssignModalOpen(true)}
                  variant="outline"
                  className="h-11 px-5 rounded-lg"
                >
                  <Building2 className="mr-2 h-4 w-4" /> Toplu Ata
                </Button>
                <Button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="h-11 px-5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                >
                  <Plus className="mr-2 h-4 w-4" /> Yeni Playlist
                </Button>
              </div>
            </div>

            {/* Toolbar */}
            <div className="flex items-center">
              <div className="text-sm text-muted-foreground">
                Toplam {filteredPlaylists.length} playlist bulunuyor
              </div>
            </div>

            {/* Playlist Tablosu */}
            {filteredPlaylists.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 border rounded-lg bg-background">
                <p className="text-lg font-medium mb-2">Playlist Bulunamadı</p>
                <p className="text-muted-foreground text-center max-w-md">
                  {searchQuery
                    ? 'Arama kriterlerinize uygun playlist bulunamadı. Lütfen farklı bir arama yapmayı deneyin.'
                    : 'Henüz hiç playlist eklenmemiş. Yeni bir playlist eklemek için "Yeni Playlist" butonunu kullanın.'}
                </p>
              </div>
            ) : (
              <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
                <Table className="w-full table-fixed">
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-50 hover:to-slate-100 border-b border-slate-200">
                      <TableHead className="py-4 pl-6 text-slate-700 font-bold text-sm w-[280px]">Playlist Bilgileri</TableHead>
                      <TableHead className="py-4 text-slate-700 font-bold text-sm w-[100px]">Şarkı Sayısı</TableHead>
                      <TableHead className="py-4 text-slate-700 font-bold text-sm w-[100px]">Toplam Süre</TableHead>
                      <TableHead className="py-4 text-slate-700 font-bold text-sm w-[100px]">Tür</TableHead>
                      <TableHead className="text-right py-4 pr-6 text-slate-700 font-bold text-sm w-[80px]">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPlaylists.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((playlist: Playlist) => (
                      <TableRow key={playlist._id} className="border-b border-slate-100 hover:bg-blue-50/30 transition-colors">
                        <TableCell className="py-4 pl-6">
                          <div className="flex items-center gap-3">
                            {playlist.artwork ? (
                              <img
                                src={playlist.artwork.startsWith('https://') ? playlist.artwork : `https://${playlist.artwork}`}
                                alt={playlist.name}
                                className="h-12 w-12 rounded-md object-cover"
                              />
                            ) : (
                              <div className="h-12 w-12 rounded-md bg-slate-200 flex items-center justify-center">
                                <Music className="h-5 w-5 text-slate-400" />
                              </div>
                            )}
                            <div className="flex flex-col">
                              <div className="flex items-center gap-1.5">
                                <span
                                  className="font-semibold text-slate-900 break-words text-left cursor-pointer hover:text-purple-600 transition-colors"
                                  onClick={() => handleViewPlaylistDetail(playlist)}
                                >
                                  {playlist.name}
                                </span>
                                {heroPlaylistIds.has(playlist._id) && (
                                  <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                                )}
                              </div>
                              {playlist.description && (
                                <span className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                                  {playlist.description}
                                </span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="py-4">
                          <span className="text-slate-700">{playlist.songs.length} şarkı</span>
                        </TableCell>
                        <TableCell className="py-4">
                          <span className="text-slate-700">
                            {playlist.totalDuration
                              ? formatDurationWithHours(playlist.totalDuration)
                              : "00:00:00"}
                          </span>
                        </TableCell>
                        <TableCell className="py-4">
                          <span className="text-slate-700">{playlist.genre || "-"}</span>
                        </TableCell>
                        <TableCell className="text-right py-4 pr-6">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-9 w-9 p-0 rounded-lg hover:bg-slate-100">
                                <span className="sr-only">Menüyü aç</span>
                                <MoreHorizontal className="h-5 w-5 text-slate-600" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuItem onClick={() => {
                                setPlaylistForHero(playlist);
                                setIsSendToHeroDialogOpen(true);
                              }}>
                                {heroPlaylistIds.has(playlist._id) ? (
                                  <>
                                    <Star className="mr-2 h-4 w-4 text-yellow-500 fill-yellow-500" />
                                    <span>Hero Ayarları</span>
                                  </>
                                ) : (
                                  <>
                                    <Star className="mr-2 h-4 w-4 text-yellow-500" />
                                    <span>Hero'ya Gönder</span>
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {
                                setPlaylistToAssign(playlist);
                                setIsAssignModalOpen(true);
                              }}>
                                <Building2 className="mr-2 h-4 w-4 text-slate-500" />
                                <span>Firmalara Ata</span>
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setPlaylistToDelete(playlist._id)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                <span>Sil</span>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Sayfalama kontrolleri */}
            {filteredPlaylists.length > itemsPerPage && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                <div className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-800">{filteredPlaylists.length}</span> playlist içinden{" "}
                  <span className="font-semibold text-slate-800">{(currentPage - 1) * itemsPerPage + 1}</span>-
                  <span className="font-semibold text-slate-800">{Math.min(currentPage * itemsPerPage, filteredPlaylists.length)}</span>{" "}
                  arası gösteriliyor
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (currentPage > 1) {
                        setCurrentPage(currentPage - 1);
                      }
                    }}
                    disabled={currentPage === 1}
                    className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                    aria-label="Önceki sayfa"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">Önceki</span>
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, Math.ceil(filteredPlaylists.length / itemsPerPage)) }, (_, i) => {
                      // Sayfa düğmeleri için başlangıç sayfasını hesapla
                      let startPage = Math.max(1, currentPage - 2);
                      const totalPages = Math.ceil(filteredPlaylists.length / itemsPerPage);

                      if (currentPage > totalPages - 2) {
                        startPage = Math.max(1, totalPages - 4);
                      }

                      const page = startPage + i;
                      if (page <= totalPages) {
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(page)}
                            className={`h-10 min-w-10 px-3 rounded-lg ${
                              currentPage === page
                                ? "bg-blue-600 hover:bg-blue-700 text-white pointer-events-none"
                                : "border-slate-200 hover:bg-slate-50"
                            }`}
                          >
                            {page}
                          </Button>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const totalPages = Math.ceil(filteredPlaylists.length / itemsPerPage);
                      if (currentPage < totalPages) {
                        setCurrentPage(currentPage + 1);
                      }
                    }}
                    disabled={currentPage === Math.ceil(filteredPlaylists.length / itemsPerPage)}
                    className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                    aria-label="Sonraki sayfa"
                  >
                    <span className="hidden sm:inline">Sonraki</span>
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}

            <AlertDialog
              open={!!playlistToDelete}
              onOpenChange={() => setPlaylistToDelete(null)}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Playlist'i Sil</AlertDialogTitle>
                  <AlertDialogDescription>
                    Bu playlist'i silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>İptal</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => playlistToDelete && handleDelete(playlistToDelete)}
                  >
                    Sil
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <CreatePlaylistModal
              isOpen={isCreateModalOpen}
              onClose={() => {
                setIsCreateModalOpen(false);
                setSelectedPlaylist(null);
              }}
              onSuccess={() => {
                refetch();
                setSelectedPlaylist(null);
              }}
              initialData={selectedPlaylist ? {
                id: selectedPlaylist._id, 
                _id: selectedPlaylist._id, 
                name: selectedPlaylist.name,
                description: selectedPlaylist.description,
                artwork: selectedPlaylist.artwork || undefined,
                songs: selectedPlaylist.songs.map(song => song._id)
              } : undefined}
              mode={selectedPlaylist ? 'edit' : 'create'}
            />

            <AssignCompaniesModal
              isOpen={isAssignModalOpen}
              onClose={() => {
                setIsAssignModalOpen(false);
                setPlaylistToAssign(null);
              }}
              onAssign={async (companies: Company[]) => {
                if (!playlistToAssign?._id) {
                  toast({
                    variant: 'destructive',
                    title: 'Hata',
                    description: 'Playlist seçilmedi',
                  });
                  return;
                }

                try {
                  // Seçilen firmaların ID'lerini al
                  const companyIds = companies.map(company => company._id);
                  console.log('Seçilen firma IDleri:', companyIds);

                  const response = await fetch(
                    `${import.meta.env.VITE_API_URL}/playlists/${playlistToAssign._id}/companies`,
                    {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                      },
                      body: JSON.stringify({ companies: companyIds })
                    }
                  );

                  if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Firmalar atanamadı');
                  }

                  const updatedPlaylist = await response.json();
                  console.log('Güncellenmiş playlist:', updatedPlaylist);

                  toast({
                    title: 'Başarılı',
                    description: 'Firmalar başarıyla atandı',
                  });

                  refetch(); // Playlist listesini yenile
                } catch (error) {
                  console.error('Firma atama hatası:', error);
                  toast({
                    variant: 'destructive',
                    title: 'Hata',
                    description: error instanceof Error ? error.message : 'Firmalar atanırken bir hata oluştu',
                  });
                } finally {
                  setPlaylistToAssign(null);
                  setIsAssignModalOpen(false);
                }
              }}
              selectedCompanies={playlistToAssign?.companies.map(id => ({ _id: id } as Company)) || []}
            />
          </>
        ) : (
          // Playlist Detay Görünümü
          <>
            <div className="mb-2">
              <button 
                onClick={handleBackToList}
                className="text-sm text-slate-500 hover:text-slate-700 flex items-center"
              >
                <ArrowLeft className="h-3 w-3 mr-1" /> Playlist Listesine Dön
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold tracking-tight">
                  {currentPlaylistDetail?.name}
                </h2>
                <p className="text-muted-foreground">
                  {currentPlaylistDetail?.description || 'Açıklama yok'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (currentPlaylistDetail) {
                      setPlaylistToAssign(currentPlaylistDetail);
                      setIsAssignModalOpen(true);
                    }
                  }}
                >
                  <Building2 className="mr-2 h-4 w-4" /> Firmalara Ata
                </Button>
              </div>
            </div>

            {/* Playlist Detay Kartı */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Sol Taraf - Playlist Bilgileri */}
              <div className="md:col-span-1">
                <div className="bg-white rounded-lg border shadow-sm">
                  <div className="p-4 border-b">
                    <div className="relative group">
                      {currentPlaylistDetail?.artwork ? (
                        <img
                          src={currentPlaylistDetail.artwork.startsWith('https://') ? currentPlaylistDetail.artwork : `https://${currentPlaylistDetail.artwork}`}
                          alt={currentPlaylistDetail.name}
                          className="w-full aspect-square object-cover rounded-md"
                        />
                      ) : (
                        <div className="w-full aspect-square bg-slate-200 rounded-md flex items-center justify-center">
                          <Music className="h-12 w-12 text-slate-400" />
                        </div>
                      )}
                      <label 
                        className="absolute top-2 right-2 bg-white rounded-full p-1.5 shadow-md opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        title="Kapak Resmini Değiştir"
                      >
                        <Edit className="h-4 w-4 text-slate-600" />
                        <input 
                          type="file" 
                          accept="image/*" 
                          className="hidden" 
                          onChange={handleArtworkChange}
                        />
                      </label>
                      {isUpdating && artworkFile && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-md">
                          <div className="text-white text-sm">Güncelleniyor...</div>
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div className="group relative">
                        <div className="flex justify-between items-start">
                          <div className="w-full">
                            <h3 className="text-sm font-medium text-slate-500">Playlist Adı</h3>
                            {isEditingName ? (
                              <div className="flex items-center mt-1">
                                <Input
                                  value={editedName}
                                  onChange={(e) => setEditedName(e.target.value)}
                                  className="h-8 text-base"
                                  disabled={isUpdating}
                                  autoFocus
                                />
                                <div className="flex items-center ml-2">
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-8 px-2" 
                                    onClick={() => setIsEditingName(false)}
                                    disabled={isUpdating}
                                  >
                                    İptal
                                  </Button>
                                  <Button 
                                    variant="default" 
                                    size="sm" 
                                    className="h-8 px-2 ml-1" 
                                    onClick={handleUpdatePlaylistName}
                                    disabled={isUpdating || !editedName.trim()}
                                  >
                                    Kaydet
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start">
                                <p className="text-base font-semibold flex-1">{currentPlaylistDetail?.name}</p>
                                <button 
                                  className="opacity-0 group-hover:opacity-100 transition-opacity ml-2"
                                  onClick={() => setIsEditingName(true)}
                                  title="Playlist Adını Değiştir"
                                >
                                  <Edit className="h-3.5 w-3.5 text-slate-400" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="group relative">
                        <div className="flex justify-between items-start">
                          <div className="w-full">
                            <h3 className="text-sm font-medium text-slate-500">Açıklama</h3>
                            {isEditingDescription ? (
                              <div className="flex items-start mt-1">
                                <textarea
                                  value={editedDescription}
                                  onChange={(e) => setEditedDescription(e.target.value)}
                                  className="min-h-[80px] w-full rounded-md border border-gray-300 bg-background px-3 py-2 text-sm resize-none"
                                  disabled={isUpdating}
                                  autoFocus
                                />
                                <div className="flex flex-col ml-2">
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-8 px-2" 
                                    onClick={() => setIsEditingDescription(false)}
                                    disabled={isUpdating}
                                  >
                                    İptal
                                  </Button>
                                  <Button 
                                    variant="default" 
                                    size="sm" 
                                    className="h-8 px-2 mt-1" 
                                    onClick={handleUpdatePlaylistDescription}
                                    disabled={isUpdating}
                                  >
                                    Kaydet
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start">
                                <p className="text-base flex-1">{currentPlaylistDetail?.description || 'Açıklama yok'}</p>
                                <button 
                                  className="opacity-0 group-hover:opacity-100 transition-opacity ml-2"
                                  onClick={() => {
                                    setEditedDescription(currentPlaylistDetail?.description || '');
                                    setIsEditingDescription(true);
                                  }}
                                  title="Açıklamayı Değiştir"
                                >
                                  <Edit className="h-3.5 w-3.5 text-slate-400" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-slate-500">Şarkı Sayısı</h3>
                        <p className="text-base">{currentPlaylistDetail?.songs.length || 0} şarkı</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-slate-500">Toplam Süre</h3>
                        <p className="text-base">
                          {currentPlaylistDetail?.totalDuration
                            ? formatDurationWithHours(currentPlaylistDetail.totalDuration)
                            : "00:00:00"}
                        </p>
                      </div>
                      {currentPlaylistDetail?.genre && (
                        <div>
                          <h3 className="text-sm font-medium text-slate-500">Tür</h3>
                          <p className="text-base">{currentPlaylistDetail.genre}</p>
                        </div>
                      )}
                      <div>
                        <h3 className="text-sm font-medium text-slate-500">Görünürlük</h3>
                        <p className="text-base">{currentPlaylistDetail?.isPublic ? 'Açık' : 'Özel'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sağ Taraf - Şarkı Listesi */}
              <div className="md:col-span-3">
                <div className="bg-white rounded-lg border shadow-sm">
                  <div className="p-4 border-b flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Şarkı Listesi</h3>
                    <div className="flex items-center gap-2">
                      <div className="relative w-64 mr-2">
                        <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
                        <Input
                          type="text"
                          placeholder="Şarkı ara..."
                          value={songSearchQuery}
                          onChange={(e) => setSongSearchQuery(e.target.value)}
                          className="pl-9 h-9"
                        />
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsAddSongsModalOpen(true)}
                        disabled={!currentPlaylistDetail}
                      >
                        <ListPlus className="mr-2 h-4 w-4" /> Şarkı Ekle
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRemoveSongsFromPlaylist}
                        disabled={selectedSongs.length === 0}
                        className="text-red-600 hover:text-red-700"
                      >
                        <ListMinus className="mr-2 h-4 w-4" /> Seçilenleri Çıkar
                      </Button>
                    </div>
                  </div>

                  {/* Şarkı Tablosu */}
                  {filteredSongs.length === 0 ? (
                    <div className="p-8 text-center">
                      {songSearchQuery ? (
                        <p className="text-slate-500">Arama kriterlerine uygun şarkı bulunamadı.</p>
                      ) : (
                        <p className="text-slate-500">Bu playlist'te henüz şarkı bulunmuyor.</p>
                      )}
                    </div>
                  ) : (
                    <Table className="w-full">
                      <TableHeader>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                          <TableHead className="w-[40px]">
                            <input
                              type="checkbox"
                              checked={selectedSongs.length > 0 && selectedSongs.length === filteredSongs.length}
                              onChange={(e) => handleSelectAllSongs(e.target.checked)}
                              className="rounded border-slate-300"
                            />
                          </TableHead>
                          <TableHead className="w-[40%]">Şarkı</TableHead>
                          <TableHead className="w-[25%]">Sanatçı</TableHead>
                          <TableHead className="w-[25%]">Albüm</TableHead>
                          <TableHead className="w-[10%] text-right">Süre</TableHead>
                          <TableHead className="text-right w-[100px]">İşlemler</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredSongs.map((song) => (
                          <TableRow key={song._id} className="group">
                            <TableCell className="w-[40px]">
                              <input
                                type="checkbox"
                                checked={selectedSongs.includes(song._id)}
                                onChange={() => handleSongSelection(song._id)}
                                className="rounded border-slate-300"
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center">
                                <div className="relative mr-3 group">
                                  {song.artworkUrl ? (
                                    <img 
                                      src={song.artworkUrl.startsWith('https://') ? song.artworkUrl : `https://${song.artworkUrl}`} 
                                      alt={song.name} 
                                      className="h-10 w-10 rounded object-cover"
                                    />
                                  ) : (
                                    <div className="h-10 w-10 rounded bg-slate-200 flex items-center justify-center">
                                      <Music className="h-5 w-5 text-slate-400" />
                                    </div>
                                  )}
                                  <div 
                                    className="absolute inset-0 bg-black bg-opacity-40 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                    onClick={() => handlePlaySong(song)}
                                  >
                                    {isPlaying && currentSong?._id === song._id ? (
                                      <Pause className="h-5 w-5 text-white" />
                                    ) : (
                                      <Play className="h-5 w-5 text-white" />
                                    )}
                                  </div>
                                </div>
                                <span className="font-medium">{song.name}</span>
                              </div>
                            </TableCell>
                            <TableCell>{song.artist || "-"}</TableCell>
                            <TableCell>{song.album || "-"}</TableCell>
                            <TableCell className="text-right">{song.duration ? formatDurationWithHours(song.duration) : "-"}</TableCell>
                            <TableCell className="w-[100px] text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={() => {
                                  setSelectedSongs([song._id]);
                                  handleRemoveSongsFromPlaylist();
                                }}
                                title="Şarkıyı Kaldır"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        <AlertDialog
          open={!!playlistToDelete}
          onOpenChange={() => setPlaylistToDelete(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Playlist'i Sil</AlertDialogTitle>
              <AlertDialogDescription>
                Bu playlist'i silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>İptal</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => playlistToDelete && handleDelete(playlistToDelete)}
              >
                Sil
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AssignCompaniesModal
          isOpen={isAssignModalOpen}
          onClose={() => {
            setIsAssignModalOpen(false);
            setPlaylistToAssign(null);
          }}
          onAssign={async (companies: Company[]) => {
            if (!playlistToAssign?._id) {
              toast({
                variant: 'destructive',
                title: 'Hata',
                description: 'Playlist seçilmedi',
              });
              return;
            }

            try {
              // Seçilen firmaların ID'lerini al
              const companyIds = companies.map(company => company._id);
              console.log('Seçilen firma IDleri:', companyIds);

              const response = await fetch(
                `${import.meta.env.VITE_API_URL}/playlists/${playlistToAssign._id}/companies`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                  },
                  body: JSON.stringify({ companies: companyIds })
                }
              );

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Firmalar atanamadı');
              }

              const updatedPlaylist = await response.json();
              console.log('Güncellenmiş playlist:', updatedPlaylist);

              toast({
                title: 'Başarılı',
                description: 'Firmalar başarıyla atandı',
              });

              refetch(); // Playlist listesini yenile
            } catch (error) {
              console.error('Firma atama hatası:', error);
              toast({
                variant: 'destructive',
                title: 'Hata',
                description: error instanceof Error ? error.message : 'Firmalar atanırken bir hata oluştu',
              });
            } finally {
              setPlaylistToAssign(null);
              setIsAssignModalOpen(false);
            }
          }}
          selectedCompanies={playlistToAssign?.companies.map(id => ({ _id: id } as Company)) || []}
        />

        {currentPlaylistDetail && (
          <AddSongsToPlaylistModal
            isOpen={isAddSongsModalOpen}
            onClose={() => setIsAddSongsModalOpen(false)}
            playlistId={currentPlaylistDetail._id}
            onSuccess={() => {
              // Playlist detaylarını yeniden yükle
              handleViewPlaylistDetail(currentPlaylistDetail);
              toast({
                title: 'Başarılı',
                description: 'Şarkılar playlist\'e eklendi',
              });
              // Ana playlist listesini de güncelle
              refetch();
            }}
          />
        )}

        {/* Hero'ya Gönder Dialog */}
        <SendToHeroDialog
          isOpen={isSendToHeroDialogOpen}
          onClose={() => {
            setIsSendToHeroDialogOpen(false);
            setPlaylistForHero(null);
          }}
          playlist={playlistForHero}
          onSuccess={() => {
            refetch();
            refetchHero();
          }}
        />

        {/* Toplu Playlist Atama Modal */}
        <BulkAssignPlaylistsModal
          isOpen={isBulkAssignModalOpen}
          onClose={() => setIsBulkAssignModalOpen(false)}
          onSuccess={() => {
            refetch();
          }}
        />
      </div>

      {/* Global Footer Player */}
      <GlobalFooterPlayer />
    </div>
  );
}
